import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { authStore } from '../../store/authStore';
import { useAuth } from '../../hooks/useAuth';
import { authAPI } from '../../api/auth';
import TermsAcceptanceModal from './TermsAcceptanceModal';
import PrivacyAcceptanceModal from './PrivacyAcceptanceModal';

interface PrivateRouteProps {
  children: React.ReactNode;
}

/**
 * PrivateRoute component
 * Redirects to login if user is not authenticated. Also gates access behind
 * a forced Terms and Conditions / Privacy Policy acceptance for accounts
 * that predate the current version of either document (or a future version
 * bump) - registration already requires acceptance of both up front, this
 * covers everyone else. Terms is checked first, then Privacy, so a user who
 * is behind on both only ever sees one modal at a time.
 */
export const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated } = authStore();
  const { user, currentUserLoading, refetchUser } = useAuth();
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptingPrivacy, setAcceptingPrivacy] = useState(false);
  const [acceptPrivacyError, setAcceptPrivacyError] = useState<string | null>(null);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for the fresh /users/me/ fetch before deciding - avoids a flash of
  // the gate against a stale cached user object that predates these fields.
  if (
    currentUserLoading &&
    (user?.has_accepted_current_terms === undefined || user?.has_accepted_current_privacy === undefined)
  ) {
    return null;
  }

  if (user && user.has_accepted_current_terms === false) {
    const handleAccept = async () => {
      setAcceptingTerms(true);
      setAcceptError(null);
      try {
        await authAPI.acceptTerms();
        await refetchUser();
      } catch (err) {
        setAcceptError('No se pudo guardar tu aceptación. Intenta de nuevo.');
      } finally {
        setAcceptingTerms(false);
      }
    };

    return (
      <TermsAcceptanceModal
        title="Actualizamos nuestros Términos y Condiciones"
        onAccept={handleAccept}
        loading={acceptingTerms}
        error={acceptError}
      />
    );
  }

  if (user && user.has_accepted_current_privacy === false) {
    const handleAccept = async () => {
      setAcceptingPrivacy(true);
      setAcceptPrivacyError(null);
      try {
        await authAPI.acceptPrivacy();
        await refetchUser();
      } catch (err) {
        setAcceptPrivacyError('No se pudo guardar tu aceptación. Intenta de nuevo.');
      } finally {
        setAcceptingPrivacy(false);
      }
    };

    return (
      <PrivacyAcceptanceModal
        title="Actualizamos nuestra Política de Privacidad"
        onAccept={handleAccept}
        loading={acceptingPrivacy}
        error={acceptPrivacyError}
      />
    );
  }

  return <>{children}</>;
};

export default PrivateRoute;
